<script>
  import { md } from '@st-graphics/graphics-kit/utils'
  import { onMount } from 'svelte'

  let { progresscolor, width, background, links = [] } = $props()

  let sections = $state({})
  let sectionsInView = $state([])
  let innerWidth = $state(0)

  let isMobileWidth = $derived(innerWidth < 960)

  let activeSectionID = $derived(
    sectionsInView.length
      ? sectionsInView.reduce((acc, sectionID) => {
          if (!acc || sections[acc].index > sections[sectionID].index) {
            acc = sectionID
          }
          return acc
        }, null)
      : null,
  )

  let activeSectionProgress = $state(0)

  const updateProgressBar = () => {
    if (isMobileWidth || !sections[activeSectionID]) return

    if (sections[activeSectionID].collapsed) {
      const { bottom, height } = sections[activeSectionID].container.getBoundingClientRect()
      activeSectionProgress = Math.max(0.01, Math.min(1, (height - bottom) / height)).toFixed(2)
    } else {
      activeSectionProgress = 0.01
    }
  }

  const updateSectionHeights = (entries) => {
    if (isMobileWidth) return

    entries.forEach((entry) => {
      const sectionID = entry.target.getAttribute('data-id')
      updateSectionHeight(sectionID)
    })
  }

  const updateSectionHeight = (sectionID) => {
    const targetContainer = sections[sectionID].container
    if (targetContainer?.offsetHeight !== sections[sectionID].lastHeight) {
      sections[sectionID].lastHeight = targetContainer.offsetHeight
      updateProgressBar()
    }
  }

  const updateSectionInView = (entries) => {
    if (isMobileWidth) return

    entries.forEach((entry) => {
      const sectionID = entry.target.getAttribute('data-id')
      if (entry.isIntersecting) {
        if (!sectionsInView.includes(sectionID)) sectionsInView = [...sectionsInView, sectionID]
      } else {
        sectionsInView = sectionsInView.filter(
          (s) => sections[s].index !== sections[sectionID].index,
        )
      }
    })
  }

  const updateSectionCollapseState = (entries) => {
    entries.forEach((entry) => {
      const sectionID = entry.target.getAttribute('data-id')
      sections[sectionID].collapsed = entry.target.getAttribute('data-collapsed') === 'true'
      updateSectionHeight(sectionID)
    })
  }

  onMount(() => {
    const resizeObserver = new ResizeObserver(updateSectionHeights)
    const intersectObserver = new IntersectionObserver(updateSectionInView)
    const mutationObserver = new MutationObserver(updateSectionCollapseState)

    links.forEach(({ anchor: sectionID }, i) => {
      try {
        const targetSection = document.querySelector(`#${sectionID}`)
        const isCollapsibleSection = targetSection.className.indexOf('collapsible') !== -1
        sections[sectionID] = {}
        sections[sectionID].index = i

        sections[sectionID].isCollapsibleSection = isCollapsibleSection
        sections[sectionID].container = isCollapsibleSection
          ? targetSection
          : targetSection.parentElement
        sections[sectionID].container.setAttribute('data-id', sectionID)
        sections[sectionID].height = sections[sectionID].container.offsetHeight
        sections[sectionID].collapsed = false

        resizeObserver.observe(sections[sectionID].container)
        intersectObserver.observe(sections[sectionID].container)
        mutationObserver.observe(sections[sectionID].container, {
          attributeFilter: ['data-collapsed'],
        })
      } catch (e) {
        console.warn(`error in initialise navigation target (target id: ${sectionID})`, e)
      }
    })

    return () => {
      resizeObserver.disconnect()
      intersectObserver.disconnect()
      mutationObserver.disconnect()
    }
  })
</script>

<svelte:window onscroll={updateProgressBar} bind:innerWidth />
<ul
  class="sidenav-container"
  style:--sidenav-background={background}
  style:--sidenav-width={width}
  style:--sidenav-progressbar-color={progresscolor}
>
  {#each links as { title, description, anchor }}
    {@const isActiveSection = activeSectionID === anchor}
    {@const progressWidth = `${isActiveSection ? activeSectionProgress * 100 : 1}%`}
    <li class:active={isActiveSection}>
      <a href="#{anchor}">
        {#if title}
          <span class="title">{@html md(title)}</span>
        {/if}
        {#if description}
          <span>{@html md(description)}</span>
        {/if}
      </a>
      <span class="section-scroll-progress" style:width={progressWidth}></span>
    </li>
  {/each}
</ul>

<style lang="scss">
  :global {
    body {
      --sidenav-text-color: #000000;
    }

    [data-theme='dark'] {
      body {
        --sidenav-text-color: #ffffff;
      }
    }

    .article-body--sidenav {
      display: flex;

      .sidenav-container {
        flex-shrink: 0;
      }
      .article-body-footer-container {
        width: 100%;
      }
    }
  }

  .sidenav-container {
    --sidenav-background: none;
    --sidenav-width: 300px;
    --sidenav-progressbar-color: #ef4123;

    position: sticky;
    top: 0;
    border-right: 1px solid var(--border-color, unset);
    height: 100dvh;
    padding: 20px 20px 60px;
    display: none;
    overflow-y: auto;
    width: var(--sidenav-width);
    background: var(--sidenav-background);

    @media (min-width: $screen-lg) {
      display: block;
    }

    li {
      position: relative;
      font-family: var(--font-sans-serif);
      padding: 10px 0;
      border-bottom: 1px dotted color-mix(in srgb, var(--border-color, unset) 70%, transparent);
      opacity: 0.5;

      &.active {
        opacity: 1;

        .section-scroll-progress {
          opacity: 1;
        }
      }

      &:hover,
      &:focus {
        opacity: 1;
      }

      a {
        color: var(--sidenav-text-color);
        text-decoration: none;

        &:hover,
        &:focus {
          text-decoration: none;
        }

        span {
          font-size: 15px;
          line-height: 18px;
          display: block;
          letter-spacing: 1px;

          &.title {
            font-size: 12px;
          }
        }
      }

      .section-scroll-progress {
        position: absolute;
        bottom: 0;
        display: block;
        height: 5px;
        opacity: 0;
        background-color: var(--sidenav-progressbar-color);
      }
    }
  }
</style>
